import { environmentHttpClient as httpClient } from "client/Api/httpClient";
import {
	HubLinkedDocumentViewModel,
	HubItemMediaPreviewViewModel,
	ReactionCounterModel,
	HubItemViewerListItemModel,
	ReactionKind,
	ReactionUserModel
} from "client/Api/Hub/common";
import {
	HubCommentOptionsModel,
	HubItemLayoutDetailsViewModel,
	HubItemMediaDetailsViewModel,
	RegisterViewModel
} from "client/Api/Hub/common.details";
import { UserIdentityModel } from "client/Api/UserProfile/service";
import { TagViewModel } from "client/Api/Tags/tags";
import { SortDirection } from "client/Api/common";
import { HubItemCategoryListItemModel } from "../hub";

export type AnnouncementDetailsViewModel = {
	announcementId: number;
	title: string;
	layout: HubItemLayoutDetailsViewModel;
	media: HubItemMediaDetailsViewModel;
	areCommentsEnabled: boolean;
	publicationStart: Date | string;
	authorId: number | null;
	authorName: string | null;
	categoryIds: readonly number[];
	isEditable: boolean;
	pinDate: string | null;
	tags: readonly TagViewModel[];
	linkedDocuments: readonly HubLinkedDocumentViewModel[] | null;
	isNew: boolean;
};

export type AnnouncementDetailsPageViewModel = {
	announcement: AnnouncementDetailsViewModel;
	commentOptions: HubCommentOptionsModel;
};

export type AnnouncementPreviewItemModel = {
	id: number;
	title: string;
	descriptionPreview: string;
	media: HubItemMediaPreviewViewModel;
	publicationStart: Date | string;
	categories: readonly HubItemCategoryListItemModel[];
	authorId: number | null;
	authorName: string | null;
	viewsCount: number;
	reactionCounters: readonly ReactionCounterModel[];
	commentsCount: number;
	areCommentsEnabled: boolean;
	pinDate: string | null;
	isNew: boolean;
};

export type AnnouncementStatisticsModel = {
	announcementId: number;
	viewsCount: number;
	reactionCounters: readonly ReactionCounterModel[];
	currentUserReaction: ReactionKind;
	commentsCount: OptionalModel<number>;
	isCurrentUserFollowingComments: boolean;
};

export type AnnouncementPagingInformationModel = {
	id: number;
	startTime: Date | string;
	viewsCount: number;
	pinDate: string | null;
};

export enum AnnouncementSortProperty {
	Views = 0,
	StartTime = 1,
	PinDate = 2
}

export type AnnouncementSortingInformationModel = {
	property: AnnouncementSortProperty;
	direction: SortDirection;
};

export type AnnouncementListQueryParameters = {
	categoryId?: number | null;
	authorId?: number | null;
	tagId?: number | null;
	searchTerm?: string | null;
	isPinned?: boolean | null;
	isNew?: boolean | null;
	sortOrder?: AnnouncementSortingInformationModel | null;
	afterAnnouncement?: AnnouncementPagingInformationModel | null;
	amount?: number | null;
};

export function list(
	parameters: AnnouncementListQueryParameters,
	signal?: AbortSignal
): Promise<LazyLoadPageResult<AnnouncementPreviewItemModel>> {
	return httpClient.get<LazyLoadPageResult<AnnouncementPreviewItemModel>>(
		"UiApi/Hub/Announcements",
		parameters,
		{ signal }
	);
}

export function getRelevantItems(
	id: number,
	targetAnnouncement: AnnouncementPagingInformationModel,
	categoryId: number | null,
	authorId: number | null,
	tagId: number | null,
	searchTerm: string | null,
	sortOrder: AnnouncementSortingInformationModel | null,
	isNew: boolean | null,
	signal?: AbortSignal
): Promise<readonly AnnouncementPreviewItemModel[]> {
	return httpClient.get<readonly AnnouncementPreviewItemModel[]>(
		`UiApi/Hub/Announcements/${id}/Relevant`,
		{
			targetAnnouncement,
			categoryId,
			tagId,
			authorId,
			searchTerm,
			sortOrder,
			isNew
		},
		{ signal }
	);
}

export function getStatistics(id: number, signal?: AbortSignal): Promise<AnnouncementStatisticsModel> {
	return httpClient.get<AnnouncementStatisticsModel>(
		`UiApi/Hub/Announcements/${id}/Statistics`,
		undefined,
		{signal}
	);
}

export function registerView(
	id: number,
	model: RegisterViewModel,
	signal?: AbortSignal
): Promise<void> {
	return httpClient.post<void>(
		`UiApi/Hub/Announcements/${id}/Views`,
		model,
		{ signal }
	);
}

export function registerReaction(
	id: number,
	kind: ReactionKind,
	signal?: AbortSignal
): Promise<AnnouncementStatisticsModel> {
	return httpClient.post<AnnouncementStatisticsModel>(
		`UiApi/Hub/Announcements/${id}/React`,
		kind,
		{ signal }
	);
}

export function updateReaction(
	id: number,
	kind: ReactionKind,
	signal?: AbortSignal
): Promise<AnnouncementStatisticsModel> {
	return httpClient.put<AnnouncementStatisticsModel>(
		`UiApi/Hub/Announcements/${id}/React`,
		kind,
		{ signal }
	);
}

export function unregisterReaction(
	id: number,
	signal?: AbortSignal
): Promise<AnnouncementStatisticsModel> {
	return httpClient.delete<AnnouncementStatisticsModel>(
		`UiApi/Hub/Announcements/${id}/React`,
		undefined,
		{ signal }
	);
}

export function registerFollowComments(
	id: number,
	signal?: AbortSignal
): Promise<AnnouncementStatisticsModel> {
	return httpClient.post<AnnouncementStatisticsModel>(
		`UiApi/Hub/Announcements/${id}/FollowComments`,
		undefined,
		{ signal }
	);
}

export function unregisterFollowComments(
	id: number,
	signal?: AbortSignal
): Promise<AnnouncementStatisticsModel> {
	return httpClient.delete<AnnouncementStatisticsModel>(
		`UiApi/Hub/Announcements/${id}/FollowComments`,
		undefined,
		{ signal }
	);
}

export function getAnnouncementDetails(
	announcementId: number,
	signal?: AbortSignal
): Promise<AnnouncementDetailsPageViewModel> {
	return httpClient.get<AnnouncementDetailsPageViewModel>(
		`UiApi/Hub/Announcements/${announcementId}/Details`,
		undefined,
		{signal}
	);
}

export function getReactionCounts(
	announcementId: number,
	signal?: AbortSignal
): Promise<readonly ReactionCounterModel[]> {
	return httpClient.get<readonly ReactionCounterModel[]>(
		`UiApi/Hub/Announcements/${announcementId}/ReactionCounts`,
		undefined,
		{signal}
	);
}

export function getReactions(
	announcementId: number,
	pageSize: number,
	skip: number,
	kind: ReactionKind | undefined,
	signal?: AbortSignal
): Promise<LazyLoadPageResult<ReactionUserModel>> {
	return httpClient.get<LazyLoadPageResult<ReactionUserModel>>(
		`UiApi/Hub/Announcements/${announcementId}/Reactions`,
		{
			kind,
			pageSize,
			skip
		},
		{signal}
	);
}

export function doesUserHaveAccess(
	announcementId: number,
	userId: number,
	signal?: AbortSignal
): Promise<boolean> {
	return httpClient.get<boolean>(
		`UiApi/Hub/Announcements/${announcementId}/DoesUserHaveAccess`,
		{
			userId
		},
		{signal}
	);
}

export function findUsers(
	announcementId: number,
	searchTerm: string,
	amount: number,
	signal?: AbortSignal
): Promise<readonly UserIdentityModel[]> {
	return httpClient.get<readonly UserIdentityModel[]>(
		`UiApi/Hub/Announcements/${announcementId}/Users`,
		{
			searchTerm,
			amount
		},
		{ signal }
	);
}

export function listAuthors(
	signal?: AbortSignal
): Promise<readonly UserIdentityModel[]> {
	return httpClient.get<readonly UserIdentityModel[]>(
		`UiApi/Hub/Announcements/Authors`,
		undefined,
		{ signal }
	);
}

export function listViewers(
	announcementId: number,
	skip: number,
	take: number,
	signal?: AbortSignal
): Promise<LazyLoadPageResult<HubItemViewerListItemModel>> {
	return httpClient.get<LazyLoadPageResult<HubItemViewerListItemModel>>(
		`UiApi/Hub/Announcements/${announcementId}/Viewers`,
		{
			skip,
			take
		},
		{ signal }
	);
}